import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AiDocAttach, AiDocChip } from "@/components/ai/AiDocAttach";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, Mic, Square, Send, ArrowLeft, Bot, User as UserIcon, Wrench, FilePlus2, Paperclip } from "lucide-react";

const MODELS = [
  { id: "gpt-5.4", label: "GPT-5.4" },
  { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
];

function SignInviaBtn({ a }) {
  const [st, setSt] = useState("pronto");
  const invia = async () => {
    if (!window.confirm(`${a.label}?\nIl firmatario riceverà il link e firmerà con codice OTP via SMS (firma elettronica semplice).`)) return;
    setSt("invio");
    try {
      const { data } = await api.post("/ai/sign/invia", { bozza_id: a.bozza_id });
      const ko = Object.entries(data.esito_invio || {}).filter(([, v]) => v === false || v?.ok === false).map(([k]) => k);
      setSt("inviata");
      ko.length ? toast.warning(`Richiesta inviata, ma non partito: ${ko.join(", ")}`) : toast.success("Richiesta di firma inviata");
    } catch (e) { setSt("pronto"); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <button onClick={invia} disabled={st !== "pronto"} title={a.label} data-testid={`ai-sign-invia-${a.bozza_id}`}
      className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 transition-colors">
      <Send className="h-3.5 w-3.5" /> {st === "inviata" ? "Inviata" : st === "invio" ? "Invio…" : "Invia ora"}
    </button>
  );
}

export default function AssistenteAI() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [model, setModel] = useState("gpt-5.4");
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [doc, setDoc] = useState(null);
  const [docInviato, setDocInviato] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    const nuovoDoc = doc && doc.id !== docInviato ? doc : null;
    setMessages((m) => [...m, { role: "user", content: msg, doc: nuovoDoc?.nome }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/assistant", { session_id: sessionId, message: msg, model, document_id: nuovoDoc?.id || null });
      if (nuovoDoc) setDocInviato(nuovoDoc.id);
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.answer, tools: data.tools_used, actions: data.actions }]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ Assistente non disponibile al momento." }]);
    } finally { setLoading(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "voce.webm");
        try {
          toast.loading("Trascrizione in corso…", { id: "stt" });
          const { data } = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          toast.dismiss("stt");
          if (data.text) { setInput(data.text); send(data.text); }
          else toast.error("Nessun testo riconosciuto.");
        } catch (e) { toast.dismiss("stt"); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      };
      mediaRef.current = mr; mr.start(); setRecording(true);
    } catch { toast.error("Microfono non disponibile o permesso negato."); }
  };
  const stopRec = () => { mediaRef.current?.stop(); setRecording(false); };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="assistente-ai-page">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="ai-back-btn"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex items-center gap-2 text-violet-700"><Sparkles className="h-5 w-5" /><span className="font-semibold text-slate-800">Assistente AI</span></div>
        <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="ai-model-select"
          className="ml-auto text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
          {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-6 max-w-3xl w-full mx-auto space-y-4" data-testid="ai-messages">
        {messages.length === 0 && (
          <div className="text-center text-slate-500 mt-16">
            <Bot className="h-10 w-10 mx-auto mb-3 text-violet-300" />
            <p className="font-medium text-slate-700">Come posso aiutarti?</p>
            <p className="text-sm mt-1">Posso cercare clienti, consultare i loro dati, validare P.IVA, analizzare la polizza della concorrenza (allega il PDF con la graffetta) e preparare bozze di pratica o MUP da confermare — anche a voce.</p>
            <div className="flex flex-wrap gap-2 justify-center mt-4 text-sm">
              {["Cerca il cliente Anffas", "Valida la P.IVA 00621450923", "Prepara una bozza MUP Danni ai Beni per Anffas"].map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="ai-suggestion"
                  className="px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:bg-violet-50 hover:border-violet-200 transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}`}>
            {m.role === "assistant" && <div className="h-8 w-8 rounded-full bg-violet-100 flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-violet-700" /></div>}
            <div className={`rounded-2xl px-4 py-2.5 max-w-[80%] whitespace-pre-wrap text-sm ${m.role === "user" ? "bg-violet-600 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
              {m.doc && <div className="mb-1 text-xs opacity-80 flex items-center gap-1" data-testid="ai-msg-doc"><Paperclip className="h-3 w-3" /> {m.doc}</div>}
              {m.role === "assistant"
                ? <div className="ai-md whitespace-normal"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown></div>
                : m.content}
              {m.tools?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-400 flex items-center gap-1">
                  <Wrench className="h-3 w-3" /> {m.tools.map((t) => t.tool).join(", ")}
                </div>
              )}
              {m.actions?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap gap-2">
                  {m.actions.map((a, j) => a.tipo === "sign_invia" ? (
                    <SignInviaBtn key={j} a={a} />
                  ) : (
                    <button key={j} onClick={() => navigate(a.deep_link)} data-testid="ai-action-btn"
                      className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-violet-600 text-white hover:bg-violet-700 transition-colors">
                      <FilePlus2 className="h-3.5 w-3.5" /> {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {m.role === "user" && <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0"><UserIcon className="h-4 w-4 text-slate-600" /></div>}
          </div>
        ))}
        {loading && <div className="flex gap-3"><div className="h-8 w-8 rounded-full bg-violet-100 flex items-center justify-center"><Bot className="h-4 w-4 text-violet-700" /></div><div className="rounded-2xl px-4 py-3 bg-white border border-slate-200"><span className="inline-flex gap-1"><span className="h-2 w-2 bg-violet-300 rounded-full animate-bounce" /><span className="h-2 w-2 bg-violet-300 rounded-full animate-bounce [animation-delay:150ms]" /><span className="h-2 w-2 bg-violet-300 rounded-full animate-bounce [animation-delay:300ms]" /></span></div></div>}
        <div ref={endRef} />
      </div>

      <div className="border-t border-slate-200 bg-white px-4 py-3">
        {doc && <div className="max-w-3xl mx-auto"><AiDocChip doc={doc} onClear={() => { setDoc(null); setDocInviato(null); }} /></div>}
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <AiDocAttach doc={doc} setDoc={setDoc} />
          <Button variant={recording ? "destructive" : "outline"} size="icon" onClick={recording ? stopRec : startRec}
            data-testid="ai-mic-btn" title={recording ? "Ferma" : "Parla"}>
            {recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </Button>
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={recording ? "Sto ascoltando…" : doc ? "Es. Analizza questa polizza e preparami il MUP" : "Scrivi un messaggio…"} data-testid="ai-input" className="flex-1" />
          <Button onClick={() => send()} disabled={loading || !input.trim()} data-testid="ai-send-btn"><Send className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
